import type { RecoveryOffer } from "@/api/recoveryOffer";
import type { RescueCopy } from "@/checkout/recovery";
import { formatUsd } from "@/checkout/requirements";
import { Button } from "@penmate/react-shared/ui/button";
import {
  Dialog,
  DialogBottomSheetContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { GiftIcon, ImageIcon, RefreshCwIcon, TagIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

// Every term and every line of offer copy comes from the server payload; the
// component only lays them out. Accept is the one decision; every other way
// out — the secondary button ("Not now" from the server), the backdrop,
// Escape — is `onNotNow`: the sheet closes and the offer stays open on the
// server. There is no in-session decline.

type RecoverySheetBodyProps = {
  offer: RecoveryOffer;
  mode: "offer" | "rescue";
  onDisplayed: (issuanceId: string) => void;
  onAccept: () => Promise<void>;
  onRescue: () => Promise<void>;
  onNotNow: () => void;
  onBusyChange: (busy: boolean) => void;
};

// Mounts only once the sheet is in the document, so the display callback
// reflects an actual render — never the trigger response.
const RecoverySheetBody = ({ offer, mode, onDisplayed, onAccept, onRescue, onNotNow, onBusyChange }: RecoverySheetBodyProps) => {
  const [accepting, setAccepting] = useState(false);
  const [rescuing, setRescuing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const displayedRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (displayedRef.current === offer.issuance_id) return;
    displayedRef.current = offer.issuance_id;
    onDisplayed(offer.issuance_id);
  }, [offer.issuance_id, onDisplayed]);

  const busy = accepting || rescuing;

  const accept = async () => {
    if (busy) return;
    setAccepting(true);
    onBusyChange(true);
    setError(null);
    try {
      await onAccept();
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e instanceof Error ? e.message : String(e));
      setAccepting(false);
      onBusyChange(false);
    }
  };

  const rescue = async () => {
    if (busy) return;
    setRescuing(true);
    onBusyChange(true);
    setError(null);
    try {
      await onRescue();
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e instanceof Error ? e.message : String(e));
      setRescuing(false);
      onBusyChange(false);
    }
  };

  // Rung 3 only when the server offered it and sent its label.
  const rescueMode = mode === "rescue" && !!offer.copy.rescue_label;

  const photosLabel = `Up to ${offer.max_photos} ${offer.max_photos === 1 ? "photo" : "photos"}`;

  return (
    <div
      className="min-h-0 flex-1 overflow-y-auto"
      style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", touchAction: "pan-y" }}
    >
      {/* The same mark the my-penmates CTA carries: accent band, tag, amber dot. */}
      <div className="pm-recovery-band relative overflow-hidden px-5 pb-5 pt-3 text-white">
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-white/40" aria-hidden="true" />
        <div className="flex items-center gap-3">
          <span className="relative inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-white/15">
            <TagIcon className="size-5" aria-hidden="true" />
            <i className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-[#ffd54f] ring-2 ring-[#009688]" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="text-lg font-semibold leading-snug" data-testid="checkout-recovery-headline">
              {offer.copy.headline}
            </div>
            {offer.copy.subheadline || offer.copy.subheadline_lead ? (
              <div className="mt-1 text-sm leading-snug text-white/85" data-testid="checkout-recovery-subheadline">
                {offer.copy.subheadline_lead ? (
                  <>
                    <span className="font-semibold text-white">{offer.copy.subheadline_lead}</span>{" "}
                  </>
                ) : null}
                {offer.copy.subheadline}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="px-5 pb-4 pt-4">
        <div className="text-sm text-muted-foreground" data-testid="checkout-recovery-body">
          {offer.copy.body}
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border bg-card px-4 py-3">
          <div className="text-2xl font-semibold tracking-tight text-foreground" data-testid="checkout-recovery-price">
            {formatUsd(offer.price_cents)}
          </div>
          {offer.max_photos > 0 ? (
            <div
              className="inline-flex items-center gap-1.5 rounded-full bg-[#009688]/10 px-2.5 py-1 text-xs font-medium text-[#00796b]"
              data-testid="checkout-recovery-max-photos"
            >
              <ImageIcon className="size-3.5" aria-hidden="true" />
              <span>{photosLabel}</span>
            </div>
          ) : null}
        </div>

        {offer.copy.fine_print ? (
          <div className="mt-3 text-xs text-muted-foreground" data-testid="checkout-recovery-fine-print">
            {offer.copy.fine_print}
          </div>
        ) : null}

        {error ? (
          <div className="ph-mask mt-3 text-xs text-destructive" data-testid="checkout-recovery-error">
            {error}
          </div>
        ) : null}

        <div className="mt-4 grid gap-2">
          <Button
            type="button"
            className="h-11 w-full bg-[#009688] text-white hover:bg-[#00897b]"
            onClick={() => void accept()}
            disabled={busy}
            data-testid="checkout-recovery-accept"
          >
            {accepting ? <RefreshCwIcon className="size-4 animate-spin" aria-hidden="true" /> : null}
            {offer.copy.accept_label}
          </Button>
          {rescueMode ? (
            <Button
              type="button"
              variant="outline"
              className="h-auto min-h-11 w-full flex-col gap-0 border border-[#009688]/40 bg-transparent py-2 text-[#00796b] hover:bg-[#009688]/10 hover:text-[#00796b]"
              onClick={() => void rescue()}
              disabled={busy}
              data-testid="checkout-recovery-rescue"
            >
              <span className="flex items-center gap-2 font-medium">
                {rescuing ? <RefreshCwIcon className="size-4 animate-spin" aria-hidden="true" /> : null}
                {offer.copy.rescue_label}
              </span>
              {offer.copy.rescue_detail ? (
                <span className="text-xs font-normal text-[#00796b]/75" data-testid="checkout-recovery-rescue-detail">
                  {offer.copy.rescue_detail}
                </span>
              ) : null}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            className="w-full text-muted-foreground"
            onClick={onNotNow}
            disabled={busy}
            data-testid="checkout-recovery-not-now"
          >
            {offer.copy.decline_label}
          </Button>
        </div>
      </div>
    </div>
  );
};

type RescueOnlySheetBodyProps = {
  copy: RescueCopy;
  onRescue: () => Promise<void>;
  onNotNow: () => void;
  onBusyChange: (busy: boolean) => void;
};

// The rescue with no offer to carry it: one decision (the grant), no terms
// to show, and "Not now" completes whatever exit was on hold. There is no
// display stamp — nothing was issued.
const RescueOnlySheetBody = ({ copy, onRescue, onNotNow, onBusyChange }: RescueOnlySheetBodyProps) => {
  const [rescuing, setRescuing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const rescue = async () => {
    if (rescuing) return;
    setRescuing(true);
    onBusyChange(true);
    setError(null);
    try {
      await onRescue();
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e instanceof Error ? e.message : String(e));
      setRescuing(false);
      onBusyChange(false);
    }
  };

  return (
    <div
      className="min-h-0 flex-1 overflow-y-auto"
      style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", touchAction: "pan-y" }}
      data-testid="checkout-recovery-rescue-only"
    >
      <div className="pm-recovery-band relative overflow-hidden px-5 pb-5 pt-3 text-white">
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-white/40" aria-hidden="true" />
        <div className="flex items-center gap-3">
          <span className="relative inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-white/15">
            <GiftIcon className="size-5" aria-hidden="true" />
            <i className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-[#ffd54f] ring-2 ring-[#009688]" aria-hidden="true" />
          </span>
          <div className="min-w-0 text-lg font-semibold leading-snug" data-testid="checkout-recovery-headline">
            {copy.headline}
          </div>
        </div>
      </div>

      <div className="px-5 pb-4 pt-4">
        <div className="text-sm text-muted-foreground" data-testid="checkout-recovery-body">
          {copy.body}
        </div>

        {error ? (
          <div className="ph-mask mt-3 text-xs text-destructive" data-testid="checkout-recovery-error">
            {error}
          </div>
        ) : null}

        <div className="mt-4 grid gap-2">
          <Button
            type="button"
            className="h-auto min-h-11 w-full flex-col gap-0 bg-[#009688] py-2 text-white hover:bg-[#00897b]"
            onClick={() => void rescue()}
            disabled={rescuing}
            data-testid="checkout-recovery-rescue"
          >
            <span className="flex items-center gap-2 font-medium">
              {rescuing ? <RefreshCwIcon className="size-4 animate-spin" aria-hidden="true" /> : null}
              {copy.rescueLabel}
            </span>
            {copy.rescueDetail ? (
              <span className="text-xs font-normal text-white/80" data-testid="checkout-recovery-rescue-detail">
                {copy.rescueDetail}
              </span>
            ) : null}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full text-muted-foreground"
            onClick={onNotNow}
            disabled={rescuing}
            data-testid="checkout-recovery-not-now"
          >
            {copy.declineLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

type SheetContent = { offer: RecoveryOffer; rescue: null } | { offer: null; rescue: RescueCopy };

export function CheckoutRecoverySheet(props: {
  open: boolean;
  offer: RecoveryOffer | null;
  // The rescue on its own; exclusive with `offer` (the offer wins if both).
  rescueOnly: RescueCopy | null;
  mode: "offer" | "rescue";
  onDisplayed: (issuanceId: string) => void;
  onAccept: () => Promise<void>;
  onRescue: () => Promise<void>;
  onNotNow: () => void;
}) {
  const { open, offer, rescueOnly, mode, onDisplayed, onAccept, onRescue, onNotNow } = props;
  const content: SheetContent | null = offer ? { offer, rescue: null } : rescueOnly ? { offer: null, rescue: rescueOnly } : null;
  // Keep the last content through the close animation.
  const [shown, setShown] = useState<SheetContent | null>(content);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (offer || rescueOnly) {
      setShown(offer ? { offer, rescue: null } : { offer: null, rescue: rescueOnly as RescueCopy });
      setBusy(false);
    }
  }, [offer, rescueOnly]);

  const isOpen = open && !!content;

  return (
    // Disable Radix scroll-lock: it can prevent scrolling in fixed/fullscreen overlays on mobile WebViews.
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        if (!next && isOpen && !busy) onNotNow();
      }}
      modal={false}
    >
      {shown ? (
        <DialogBottomSheetContent
          onPointerDownOutside={(e) => {
            if (busy) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (busy) e.preventDefault();
          }}
          data-testid="checkout-recovery-sheet"
        >
          <DialogTitle className="sr-only">{shown.offer ? shown.offer.copy.headline : shown.rescue.headline}</DialogTitle>
          <DialogDescription className="sr-only">{shown.offer ? shown.offer.copy.body : shown.rescue.body}</DialogDescription>
          {shown.offer ? (
            <RecoverySheetBody
              key={`${shown.offer.issuance_id}:${mode}`}
              offer={shown.offer}
              mode={mode}
              onDisplayed={onDisplayed}
              onAccept={onAccept}
              onRescue={onRescue}
              onNotNow={onNotNow}
              onBusyChange={setBusy}
            />
          ) : (
            <RescueOnlySheetBody key="rescue-only" copy={shown.rescue} onRescue={onRescue} onNotNow={onNotNow} onBusyChange={setBusy} />
          )}
        </DialogBottomSheetContent>
      ) : null}
    </Dialog>
  );
}
